import React, { useState, useEffect, useRef } from "react";

import Box from "@mui/material/Box";

import { useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import Skeleton from "@mui/material/Skeleton";

import { isEqualSimple } from "../utils/basicFunctions";
import { expiresIn } from "../utils/signedUrls";
import { useCountdown, useDebounce } from "usehooks-ts";

import { FilmIcon } from "../CustomIcons/CustomIcons";

import { useAuthController } from "@camberi/firecms";

const SIGNED_URL_REFRESH_MS = 1000 * 60 * 60 * 2;
const COCONUT_HOVER_COLUMNS = 5;
const SIGN_DEDUPE_TTL_MS = 30_000;

const signInFlight = new Map<string, Promise<any>>();
const signRecently = new Map<string, number>();

function clamp(number: number, min: number, max: number) {
	if (!Number.isFinite(number)) {
		return min;
	}

	return Math.max(min, Math.min(number, max));
}

const normalizeFieldPath = (path: any) => {
	return String(path || "")
		.trim()
		.replace(/\[(\w+)\]/g, ".$1")
		.replace(/^\./, "");
};

const getByPath = (obj: any, path: any) => {
	const normalizedPath = normalizeFieldPath(path);

	if (!normalizedPath) {
		return undefined;
	}

	const parts = normalizedPath.split(".");
	let current = obj;

	for (const part of parts) {
		if (!current || typeof current !== "object") {
			return undefined;
		}

		if (!Object.prototype.hasOwnProperty.call(current, part)) {
			return undefined;
		}

		current = current[part];
	}

	return current;
};

const isHttpUrl = (value: any) => {
	return typeof value === "string" && (value.startsWith("http://") || value.startsWith("https://"));
};

const isSignedUrlFieldPath = (fieldPath: any) => {
	return String(fieldPath || "").includes("_signedUrl");
};

const isSignableStoragePath = (value: any) => {
	if (!value || typeof value !== "string") {
		return false;
	}

	if (value === "error") {
		return false;
	}

	if (isHttpUrl(value)) {
		return false;
	}

	if (value.startsWith("gs://")) {
		return false;
	}

	if (value.startsWith("/") || value.includes("..") || value.includes("?")) {
		return false;
	}

	return value.startsWith("versions/");
};

const isFreshSignedUrl = (value: any) => {
	if (!value || typeof value !== "string") {
		return false;
	}

	return expiresIn(value) > SIGNED_URL_REFRESH_MS;
};

const isReadySource = (source: any) => {
	if (!source?.pathField) {
		return false;
	}

	if (isFreshSignedUrl(source.signedUrl)) {
		return true;
	}

	if (isHttpUrl(source.path)) {
		return true;
	}

	return false;
};

const isSourceSignable = (source: any) => {
	if (!source?.pathField) {
		return false;
	}

	return isSignableStoragePath(source.path);
};

const isUsableSource = (source: any) => {
	return isReadySource(source) || isSourceSignable(source);
};

const firstReadySource = (...sources: any[]) => {
	return sources.find((source) => isReadySource(source)) || null;
};

const firstSignableSource = (...sources: any[]) => {
	return sources.find((source) => isSourceSignable(source)) || null;
};

const firstBestSource = (...sources: any[]) => {
	return firstReadySource(...sources) || firstSignableSource(...sources);
};

const firstUsablePreferredSource = (...sources: any[]) => {
	return sources.find((source) => isUsableSource(source)) || null;
};

const getSignKey = (versionId: string, source: any) => {
	return `${versionId}:${source?.pathField || ""}:${source?.path || ""}`;
};

const getFioAssetId = (versionValues: any, assetId: any) => {
	if (assetId === "fav") {
		return versionValues?.cur_preview_fav || "";
	}

	return assetId || "";
};

const getCoconutUploadId = (versionValues: any, assetId: any) => {
	const coconutJobs = versionValues?.coconut_jobs || {};
	const requestedId = assetId === "fav" ? versionValues?.cur_preview_fav : assetId;

	if (requestedId && coconutJobs?.[requestedId]) {
		return requestedId;
	}

	const preferredIds = Object.entries(coconutJobs)
		.filter(([, job]: any) => {
			return Boolean(job?.preview || job?.renditions);
		})
		.map(([uploadId]) => uploadId);

	return preferredIds[0] || Object.keys(coconutJobs)[0] || "";
};

const getImageUploadId = (versionValues: any, assetId: any) => {
	const imageJobs = versionValues?.image_jobs || {};
	const requestedId = assetId === "fav" ? versionValues?.cur_preview_fav : assetId;

	if (requestedId && imageJobs?.[requestedId]) {
		return requestedId;
	}

	const preferredIds = Object.entries(imageJobs)
		.filter(([, job]: any) => {
			return Boolean(job?.preview || job?.renditions);
		})
		.map(([uploadId]) => uploadId);

	return preferredIds[0] || Object.keys(imageJobs)[0] || "";
};

const getPreviewContext = (versionValues: any, assetId: any, strictAssetId = false) => {
	const requestedId = assetId === "fav" ? versionValues?.cur_preview_fav : assetId;

	if (requestedId && versionValues?.fio_asset_ids?.[requestedId]) {
		return {
			provider: "fio",
			id: requestedId,
			data: versionValues.fio_asset_ids[requestedId],
		};
	}

	if (requestedId && versionValues?.coconut_jobs?.[requestedId]) {
		return {
			provider: "coconut",
			id: requestedId,
			data: versionValues.coconut_jobs[requestedId],
		};
	}

	if (requestedId && versionValues?.image_jobs?.[requestedId]) {
		return {
			provider: "image",
			id: requestedId,
			data: versionValues.image_jobs[requestedId],
		};
	}

	if (strictAssetId) {
		return {
			provider: "none",
			id: "",
			data: null,
		};
	}

	const fioAssetId = getFioAssetId(versionValues, assetId);

	if (fioAssetId && versionValues?.fio_asset_ids?.[fioAssetId]) {
		return {
			provider: "fio",
			id: fioAssetId,
			data: versionValues.fio_asset_ids[fioAssetId],
		};
	}

	const coconutUploadId = getCoconutUploadId(versionValues, assetId);

	if (coconutUploadId && versionValues?.coconut_jobs?.[coconutUploadId]) {
		return {
			provider: "coconut",
			id: coconutUploadId,
			data: versionValues.coconut_jobs[coconutUploadId],
		};
	}

	const imageUploadId = getImageUploadId(versionValues, assetId);

	if (imageUploadId && versionValues?.image_jobs?.[imageUploadId]) {
		return {
			provider: "image",
			id: imageUploadId,
			data: versionValues.image_jobs[imageUploadId],
		};
	}

	return {
		provider: "none",
		id: "",
		data: null,
	};
};

const getSourceFromPath = (versionValues: any, pathField: string) => {
	const normalizedPathField = normalizeFieldPath(pathField);

	if (!normalizedPathField || isSignedUrlFieldPath(normalizedPathField)) {
		return null;
	}

	return {
		pathField: normalizedPathField,
		signedUrlField: `${normalizedPathField}_signedUrl`,
		path: getByPath(versionValues, normalizedPathField),
		signedUrl: getByPath(versionValues, `${normalizedPathField}_signedUrl`),
	};
};

const listCoconutRenditions = (coconutJob: any) => {
	return Object.entries(coconutJob?.renditions || {})
		.filter(([key]) => !String(key).includes("_signedUrl"))
		.map(([key, rendition]: any) => ({
			key,
			...(rendition || {}),
		}));
};

const getCoconutRendition = (coconutJob: any, predicate: (rendition: any) => boolean) => {
	return listCoconutRenditions(coconutJob).find(predicate) || null;
};

const getCoconutRenditionSource = (versionValues: any, uploadId: string, rendition: any) => {
	if (!rendition?.previewKey && !rendition?.key) {
		return null;
	}

	const key = rendition.previewKey || rendition.key;

	if (rendition?.path) {
		return getSourceFromPath(versionValues, `coconut_jobs.${uploadId}.renditions.${key}.path`);
	}

	return getSourceFromPath(versionValues, `coconut_jobs.${uploadId}.preview.${key}`);
};

const getCoconutPreviewSource = (versionValues: any, uploadId: string, key: string) => {
	const rendition = getCoconutRendition(versionValues?.coconut_jobs?.[uploadId], (item) => {
		return item.previewKey === key || item.key === key;
	});

	if (rendition) {
		return getCoconutRenditionSource(versionValues, uploadId, rendition);
	}

	return getSourceFromPath(versionValues, `coconut_jobs.${uploadId}.preview.${key}`);
};

const listImageRenditions = (imageJob: any) => {
	return Object.entries(imageJob?.renditions || {})
		.filter(([key]) => !String(key).includes("_signedUrl"))
		.map(([key, rendition]: any) => ({
			key,
			...(rendition || {}),
		}));
};

const getImageRendition = (imageJob: any, predicate: (rendition: any) => boolean) => {
	return listImageRenditions(imageJob).find(predicate) || null;
};

const getImageRenditionSource = (versionValues: any, uploadId: string, rendition: any) => {
	if (!rendition?.previewKey && !rendition?.key) {
		return null;
	}

	const key = rendition.previewKey || rendition.key;

	if (rendition?.path) {
		return getSourceFromPath(versionValues, `image_jobs.${uploadId}.renditions.${key}.path`);
	}

	return getSourceFromPath(versionValues, `image_jobs.${uploadId}.preview.${key}`);
};

const getImagePreviewSource = (versionValues: any, uploadId: string, key: string) => {
	const rendition = getImageRendition(versionValues?.image_jobs?.[uploadId], (item) => {
		return item.previewKey === key || item.key === key;
	});

	if (rendition) {
		return getImageRenditionSource(versionValues, uploadId, rendition);
	}

	return getSourceFromPath(versionValues, `image_jobs.${uploadId}.preview.${key}`);
};

const getHoverFrameCountFromKey = (value: any) => {
	const text = String(value || "").toLowerCase();
	const match = text.match(/img_hover_(?:thumb|vtt)_(\d+)/) || text.match(/hover[_-](\d+)/);

	if (!match?.[1]) {
		return null;
	}

	const parsed = Number(match[1]);

	return Number.isFinite(parsed) ? parsed : null;
};

export const PreviewImage = React.forwardRef(function PreviewImage(props: any, ref) {
	const theme = useTheme();
	const smMatch = useMediaQuery(theme.breakpoints.up("sm"));

	const { status, assetId, version, img_type, autoplay = false, is_visible = false, is_round = false, show_favorite = false, is_selected = false, img_fit_view = false, strict_asset_id = false, sx, onclick, ...newProps } = props;

	const authController = useAuthController();

	const scrubFrameRef = useRef<HTMLDivElement | null>(null);
	const signRetryTimerRef = useRef<any>(null);

	const [signRetryNonce, setSignRetryNonce] = useState<number>(0);

	const [isScrubable, setIsScrubable] = useState<boolean>(false);
	const [scrubMode, setScrubMode] = useState<"none" | "fio_vertical" | "coconut_grid">("none");
	const [curAssetId, setCurAssetId] = useState<string>("");

	const [thumbInfo, setThumbInfo] = useState<any>(undefined);
	const [bounds, setBounds] = useState<any>(undefined);

	const [requiredSources, setRequiredSources] = useState<string[]>([]);

	const [curImgUrl, setCurImgUrl] = useState<string>("");
	const [imgLoaded, setImgLoaded] = useState<boolean>(false);
	const [imgNaturalSize, setImgNaturalSize] = useState<{ width: number; height: number }>({
		width: 0,
		height: 0,
	});

	const [curImgUrlCover, setCurImgUrlCover] = useState<string>("");
	const [imgLoadedCover, setImgLoadedCover] = useState<boolean>(false);

	const scheduleSignRetry = (delayMs = 500) => {
		if (typeof window === "undefined") {
			return;
		}

		if (signRetryTimerRef.current) {
			window.clearTimeout(signRetryTimerRef.current);
		}

		signRetryTimerRef.current = window.setTimeout(() => {
			setSignRetryNonce(Date.now());
		}, Math.max(50, delayMs));
	};

	useEffect(() => {
		return () => {
			if (typeof window !== "undefined" && signRetryTimerRef.current) {
				window.clearTimeout(signRetryTimerRef.current);
			}
		};
	}, []);

	const requestSign = async (version_id: string, src_fields: any[]) => {
		const safeSrcFields = (src_fields || []).filter((srcField) => {
			const srcPath = normalizeFieldPath(srcField?.src_path);

			if (!srcPath || isSignedUrlFieldPath(srcPath)) {
				return false;
			}

			const storagePath = getByPath(version?.values, srcPath);

			return isSignableStoragePath(storagePath);
		});

		if (!safeSrcFields.length || !version_id) {
			return null;
		}

		const response = await fetch(`${authController?.extra?.apiUrl}/resign_url`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `boostToken ${authController?.extra?.user_data?.values?.boost_token}`,
			},
			body: JSON.stringify({
				src_fields: safeSrcFields,
				version_id,
			}),
		});

		let responseBody: any = null;

		try {
			responseBody = await response.json();
		} catch {
			responseBody = null;
		}

		if (!response.ok) {
			throw {
				status: response.status,
				statusText: response.statusText,
				body: responseBody,
			};
		}

		return responseBody;
	};

	const ensureSignedSource = (source: any, setter: (value: string) => void) => {
		if (!source?.pathField) {
			return;
		}

		if (isFreshSignedUrl(source.signedUrl)) {
			setter(source.signedUrl);
			return;
		}

		if (isHttpUrl(source.path)) {
			setter(source.path);
			return;
		}

		if (!isSignableStoragePath(source.path)) {
			return;
		}

		if (!version?.id) {
			return;
		}

		const signKey = getSignKey(version.id, source);
		const recentlySignedAt = signRecently.get(signKey) || 0;
		const recentAge = Date.now() - recentlySignedAt;

		if (recentAge < SIGN_DEDUPE_TTL_MS) {
			scheduleSignRetry(SIGN_DEDUPE_TTL_MS - recentAge + 100);
			return;
		}

		if (signInFlight.has(signKey)) {
			signInFlight.get(signKey)?.finally(() => {
				scheduleSignRetry(100);
			});

			return;
		}

		const signPromise = requestSign(version.id, [{ src_path: source.pathField }]);

		signInFlight.set(signKey, signPromise);

		signPromise
			.then(() => {
				signRecently.set(signKey, Date.now());
				scheduleSignRetry(250);
			})
			.catch((err) => {
				console.log(err);
				scheduleSignRetry(1000);
			})
			.finally(() => {
				signInFlight.delete(signKey);
			});
	};

	useEffect(() => {
		const versionValues = version?.values || {};
		const context = getPreviewContext(versionValues, assetId, strict_asset_id);
		const nextAssetId = context.id || "";

		if (!isEqualSimple(nextAssetId, curAssetId)) {
			setCurAssetId(nextAssetId);
		}
	}, [assetId, version, curAssetId, strict_asset_id]);

	useEffect(() => {
		setCurImgUrl("");
		setCurImgUrlCover("");

		setImgLoaded(false);
		setImgLoadedCover(false);

		setImgNaturalSize({ width: 0, height: 0 });
		setBounds(undefined);

		setRequiredSources([]);
		setThumbInfo(undefined);
		setIsScrubable(false);
		setScrubMode("none");
	}, [curAssetId, version?.id, img_type]);

	useEffect(() => {
		if (typeof curAssetId === "undefined" || curAssetId === "") {
			setRequiredSources([]);
			setThumbInfo(undefined);
			setIsScrubable(false);
			setScrubMode("none");
			return;
		}

		const versionValues = version?.values || {};
		const context = getPreviewContext(versionValues, curAssetId, strict_asset_id);

		if (context.provider === "fio") {
			const thumbScrubSource = getSourceFromPath(versionValues, `fio_asset_ids.${curAssetId}.img_thumb_scrub`);
			const fullSource = getSourceFromPath(versionValues, `fio_asset_ids.${curAssetId}.img_full`);
			const smallSource = getSourceFromPath(versionValues, `fio_asset_ids.${curAssetId}.img_small`);
			const coverSource = getSourceFromPath(versionValues, `fio_asset_ids.${curAssetId}.img_cover`);

			let imgTypeToLoad = img_type;
			let mainSource: any = null;

			if (img_type === "thumb_scrub") {
				if (smMatch && isUsableSource(thumbScrubSource)) {
					mainSource = thumbScrubSource;
					imgTypeToLoad = "thumb_scrub";
				} else {
					mainSource = firstBestSource(fullSource, smallSource, coverSource);
					imgTypeToLoad = mainSource === fullSource ? "full" : mainSource === smallSource ? "small" : "cover";
				}
			} else if (img_type === "full") {
				mainSource = firstBestSource(fullSource, smallSource, coverSource);
				imgTypeToLoad = mainSource === fullSource ? "full" : mainSource === smallSource ? "small" : "cover";
			} else if (img_type === "small" || img_type === "thumb") {
				mainSource = firstBestSource(smallSource, coverSource, fullSource);
				imgTypeToLoad = mainSource === smallSource ? "small" : mainSource === coverSource ? "cover" : "full";
			} else {
				mainSource = firstBestSource(coverSource, smallSource, fullSource);
				imgTypeToLoad = mainSource === coverSource ? "cover" : mainSource === smallSource ? "small" : "full";
			}

			if (!mainSource) {
				setRequiredSources(curImgUrl ? ["existing"] : []);
				setThumbInfo(undefined);
				setIsScrubable(false);
				setScrubMode("none");
				return;
			}

			const isScrub = imgTypeToLoad === "thumb_scrub";
			const coverOverlaySource = firstBestSource(coverSource, smallSource, fullSource);
			const useCoverOverlay = isScrub && !autoplay && isUsableSource(coverOverlaySource);

			setThumbInfo(versionValues?.fio_asset_ids?.[curAssetId]?.thumb_info);
			setIsScrubable(isScrub);
			setScrubMode(isScrub ? "fio_vertical" : "none");
			setRequiredSources(useCoverOverlay ? ["main", "cover"] : ["main"]);

			ensureSignedSource(mainSource, setCurImgUrl);

			if (useCoverOverlay) {
				ensureSignedSource(coverOverlaySource, setCurImgUrlCover);
			}

			return;
		}

		if (context.provider === "coconut") {
			const coconutJob = context.data;

			const coverRendition = getCoconutRendition(coconutJob, (item) => {
				return item.role === "cover" || item.previewKey === "img_cover" || item.key === "img_cover";
			}) || {
				previewKey: "img_cover",
			};

			const hoverImageRendition = getCoconutRendition(coconutJob, (item) => {
				return item.role === "hover_image" || String(item.previewKey || item.key || "").startsWith("img_hover_thumb");
			});

			const coverSource = firstBestSource(getCoconutRenditionSource(versionValues, curAssetId, coverRendition), getCoconutPreviewSource(versionValues, curAssetId, "img_cover"));

			const hoverSource = hoverImageRendition
				? firstUsablePreferredSource(getCoconutRenditionSource(versionValues, curAssetId, hoverImageRendition), getCoconutPreviewSource(versionValues, curAssetId, hoverImageRendition.previewKey || hoverImageRendition.key))
				: null;

			const canUseHover = img_type === "thumb_scrub" && smMatch && Boolean(hoverImageRendition) && isUsableSource(hoverSource);
			const mainSource = canUseHover ? hoverSource : firstBestSource(coverSource, hoverSource);

			if (!mainSource) {
				setRequiredSources(curImgUrl ? ["existing"] : []);
				setThumbInfo(undefined);
				setIsScrubable(false);
				setScrubMode("none");
				return;
			}

			const hoverFrameCount = hoverImageRendition?.hoverFrameCount || hoverImageRendition?.normalized?.hoverFrameCount || getHoverFrameCountFromKey(hoverImageRendition?.previewKey) || getHoverFrameCountFromKey(hoverImageRendition?.key) || 1;

			const hoverColumns = COCONUT_HOVER_COLUMNS;
			const hoverRows = Math.max(1, Math.ceil(hoverFrameCount / hoverColumns));

			setThumbInfo({
				thumbs: hoverFrameCount,
				hoverFrameCount,
				hoverColumns,
				hoverRows,
			});

			setIsScrubable(Boolean(canUseHover));
			setScrubMode(canUseHover ? "coconut_grid" : "none");
			setRequiredSources(canUseHover && isUsableSource(coverSource) ? ["hover", "cover"] : ["main"]);

			ensureSignedSource(mainSource, setCurImgUrl);

			if (canUseHover && isUsableSource(coverSource)) {
				ensureSignedSource(coverSource, setCurImgUrlCover);
			}

			return;
		}

		if (context.provider === "image") {
			const imageJob = context.data;

			const thumbRendition = getImageRendition(imageJob, (item) => {
				return item.role === "thumbnail" || item.previewKey === "img_thumb" || item.key === "img_thumb";
			}) || {
				previewKey: "img_thumb",
			};

			const coverRendition = getImageRendition(imageJob, (item) => {
				return item.role === "cover" || item.previewKey === "img_cover" || item.key === "img_cover";
			}) || {
				previewKey: "img_cover",
			};

			const fullRendition = getImageRendition(imageJob, (item) => {
				return item.role === "max_2160" || item.previewKey === "img_2160" || item.key === "img_2160";
			}) || {
				previewKey: "img_2160",
			};

			const thumbSource = firstBestSource(
				getImageRenditionSource(versionValues, curAssetId, thumbRendition),
				getImagePreviewSource(versionValues, curAssetId, thumbRendition.previewKey || thumbRendition.key || "img_thumb"),
				getImagePreviewSource(versionValues, curAssetId, "img_thumb")
			);

			const coverSource = firstBestSource(
				getImageRenditionSource(versionValues, curAssetId, coverRendition),
				getImagePreviewSource(versionValues, curAssetId, coverRendition.previewKey || coverRendition.key || "img_cover"),
				getImagePreviewSource(versionValues, curAssetId, "img_cover")
			);

			const fullSource = firstBestSource(
				getImageRenditionSource(versionValues, curAssetId, fullRendition),
				getImagePreviewSource(versionValues, curAssetId, fullRendition.previewKey || fullRendition.key || "img_2160"),
				getImagePreviewSource(versionValues, curAssetId, "img_2160")
			);

			let selectedSource: any = null;

			if (img_type === "full") {
				selectedSource = firstBestSource(fullSource, coverSource, thumbSource);
			} else if (img_type === "thumb" || img_type === "small") {
				selectedSource = firstBestSource(thumbSource, coverSource, fullSource);
			} else {
				selectedSource = firstBestSource(coverSource, thumbSource, fullSource);
			}

			setThumbInfo(undefined);
			setIsScrubable(false);
			setScrubMode("none");

			if (!selectedSource) {
				setRequiredSources(curImgUrl ? ["existing"] : []);
				return;
			}

			setRequiredSources(["image"]);
			ensureSignedSource(selectedSource, setCurImgUrl);

			return;
		}

		setRequiredSources([]);
		setIsScrubable(false);
		setScrubMode("none");
	}, [curAssetId, version, smMatch, img_type, autoplay, strict_asset_id, signRetryNonce]);

	useEffect(() => {
		if (curImgUrl === "") {
			return;
		}

		let cancelled = false;

		setImgLoaded(false);

		const image = new Image();

		image.onload = () => {
			if (cancelled) {
				return;
			}

			setImgNaturalSize({
				width: image.naturalWidth || 0,
				height: image.naturalHeight || 0,
			});

			setImgLoaded(true);
		};

		image.onerror = () => {
			if (cancelled) {
				return;
			}

			setImgLoaded(false);
			setImgNaturalSize({ width: 0, height: 0 });
		};

		image.src = curImgUrl;

		return () => {
			cancelled = true;
		};
	}, [curImgUrl]);

	useEffect(() => {
		if (curImgUrlCover === "") {
			return;
		}

		let cancelled = false;

		setImgLoadedCover(false);

		const image = new Image();

		image.onload = () => {
			if (cancelled) {
				return;
			}

			setImgLoadedCover(true);
		};

		image.onerror = () => {
			if (cancelled) {
				return;
			}

			setImgLoadedCover(false);
		};

		image.src = curImgUrlCover;

		return () => {
			cancelled = true;
		};
	}, [curImgUrlCover]);

	const [mousePosition, setMousePosition] = useState<number[]>([-1, -1]);
	const [mouseHover, setMouseHover] = useState<boolean>(false);
	const debouncedMouseHover = useDebounce<boolean>(mouseHover, autoplay ? 150 : 0);

	const [scrubIntervalValue] = useState<number>(25);

	const [count, { startCountdown, resetCountdown }] = useCountdown({
		countStart: 100,
		intervalMs: scrubIntervalValue,
	});

	const updateMousePosition = (e: any) => {
		const nextBounds = e.currentTarget.getBoundingClientRect();

		const x = clamp(e.clientX - nextBounds.left, 0, nextBounds.width || 0);
		const y = clamp(e.clientY - nextBounds.top, 0, nextBounds.height || 0);

		setMousePosition([x, y]);
		setBounds(nextBounds);
	};

	const onMousemove = (e: any) => {
		setMouseHover(true);
		updateMousePosition(e);
		resetCountdown();
	};

	const onMouseLeave = () => {
		setMouseHover(false);
	};

	useEffect(() => {
		if (debouncedMouseHover === false && mouseHover === false) {
			if (is_visible && autoplay) {
				resetCountdown();
				startCountdown();
			}
		}
	}, [debouncedMouseHover, mouseHover, is_visible, autoplay, resetCountdown, startCountdown]);

	useEffect(() => {
		if (count === 0 && is_visible && autoplay) {
			resetCountdown();
			startCountdown();
		}
	}, [count, is_visible, autoplay, resetCountdown, startCountdown]);

	useEffect(() => {
		if (is_visible && autoplay) {
			startCountdown();
		}
	}, [is_visible, autoplay, startCountdown]);

	const allPreloaded = requiredSources.length !== 0 ? (requiredSources.length > 1 ? imgLoaded && imgLoadedCover : imgLoaded || Boolean(curImgUrl)) : false;

	const versionValues = version?.values || {};
	const previewContext = getPreviewContext(versionValues, curAssetId, strict_asset_id);

	const isVideo =
		previewContext.provider === "fio"
			? versionValues?.fio_asset_ids?.[curAssetId]?.asset_info?.fps > 0
			: previewContext.provider === "coconut"
			? listCoconutRenditions(previewContext.data).some((item) => item.mediaType === "video" || item.group === "video")
			: false;

	const getScrubFrameIndex = () => {
		const frameCount = Math.max(1, scrubMode === "coconut_grid" ? thumbInfo?.hoverFrameCount || thumbInfo?.thumbs || 1 : thumbInfo?.thumbs || 1);

		if (frameCount <= 1) {
			return 0;
		}

		if (!debouncedMouseHover && autoplay) {
			return clamp(Math.floor(((100 - count) / 100) * frameCount), 0, frameCount - 1);
		}

		const width = bounds?.width || scrubFrameRef.current?.clientWidth || 0;

		if (!width) {
			return 0;
		}

		const ratio = clamp(mousePosition[0] / width, 0, 0.999999);

		return clamp(Math.floor(ratio * frameCount), 0, frameCount - 1);
	};

	const getScrubContainerSize = () => {
		return {
			width: bounds?.width || scrubFrameRef.current?.clientWidth || 0,
			height: bounds?.height || scrubFrameRef.current?.clientHeight || 0,
		};
	};

	const getFioVerticalScrubStyle = () => {
		const thumbs = Math.max(1, Number(thumbInfo?.thumbs) || 1);
		const frameIndex = clamp(getScrubFrameIndex(), 0, thumbs - 1);

		const { width: containerWidth, height: containerHeight } = getScrubContainerSize();

		const spriteWidth = imgNaturalSize.width || 0;
		const spriteHeight = imgNaturalSize.height || 0;

		if (!containerWidth || !containerHeight || !spriteWidth || !spriteHeight) {
			const yPosition = thumbs > 1 ? (frameIndex / (thumbs - 1)) * 100 : 50;

			return {
				backgroundSize: `auto ${thumbs * 100}%`,
				backgroundPosition: `50% ${yPosition}%`,
				backgroundRepeat: "no-repeat",
			};
		}

		const cellWidth = spriteWidth;
		const cellHeight = spriteHeight / thumbs;

		const scale = Math.max(containerWidth / cellWidth, containerHeight / cellHeight);

		const scaledSpriteWidth = spriteWidth * scale;
		const scaledSpriteHeight = spriteHeight * scale;

		const cellCenterX = (cellWidth / 2) * scale;
		const cellCenterY = (frameIndex * cellHeight + cellHeight / 2) * scale;

		const backgroundPositionX = containerWidth / 2 - cellCenterX;
		const backgroundPositionY = containerHeight / 2 - cellCenterY;

		return {
			backgroundSize: `${scaledSpriteWidth}px ${scaledSpriteHeight}px`,
			backgroundPosition: `${backgroundPositionX}px ${backgroundPositionY}px`,
			backgroundRepeat: "no-repeat",
		};
	};

	const getCoconutGridScrubStyle = () => {
		const frameCount = Math.max(1, thumbInfo?.hoverFrameCount || thumbInfo?.thumbs || 1);
		const columns = Math.max(1, thumbInfo?.hoverColumns || COCONUT_HOVER_COLUMNS);
		const rows = Math.max(1, thumbInfo?.hoverRows || Math.ceil(frameCount / columns));
		const frameIndex = clamp(getScrubFrameIndex(), 0, frameCount - 1);

		const col = frameIndex % columns;
		const row = Math.floor(frameIndex / columns);

		const { width: containerWidth, height: containerHeight } = getScrubContainerSize();

		const spriteWidth = imgNaturalSize.width || 0;
		const spriteHeight = imgNaturalSize.height || 0;

		if (!containerWidth || !containerHeight || !spriteWidth || !spriteHeight) {
			return {
				backgroundSize: "cover",
				backgroundPosition: "50% 50%",
				backgroundRepeat: "no-repeat",
			};
		}

		const cellWidth = spriteWidth / columns;
		const cellHeight = spriteHeight / rows;

		const scale = Math.max(containerWidth / cellWidth, containerHeight / cellHeight);

		const scaledSpriteWidth = spriteWidth * scale;
		const scaledSpriteHeight = spriteHeight * scale;

		const cellCenterX = (col * cellWidth + cellWidth / 2) * scale;
		const cellCenterY = (row * cellHeight + cellHeight / 2) * scale;

		const backgroundPositionX = containerWidth / 2 - cellCenterX;
		const backgroundPositionY = containerHeight / 2 - cellCenterY;

		return {
			backgroundSize: `${scaledSpriteWidth}px ${scaledSpriteHeight}px`,
			backgroundPosition: `${backgroundPositionX}px ${backgroundPositionY}px`,
			backgroundRepeat: "no-repeat",
		};
	};

	const clickAssetId = curAssetId || (!strict_asset_id ? (assetId === "fav" ? version?.values?.cur_preview_fav : assetId) : "");

	return (
		<Box
			sx={{
				...sx,
				...(is_selected && { border: 2, borderColor: `${theme.palette.primary.dark}` }),
				...(is_round && { borderRadius: "18px!important", overflow: "hidden!important" }),
			}}
			{...newProps}
			ref={ref}
			onMouseEnter={onMousemove}
			onMouseMove={onMousemove}
			onMouseLeave={onMouseLeave}
			onClick={() => {
				if (typeof onclick !== "undefined") {
					onclick();
				}

				if (!clickAssetId) {
					return;
				}

				authController?.extra?.playerDialogHandleOpen(version?.values?.asset?.id, version?.id, clickAssetId);
			}}
		>
			{allPreloaded ? (
				isScrubable ? (
					<Box sx={{ ...(is_round && { borderRadius: 4, overflow: "hidden" }), position: "relative", width: "100%", height: "100%" }}>
						<Box
							ref={scrubFrameRef}
							style={{
								cursor: "pointer",
								position: "relative",
								width: "100%",
								height: "100%",
								backgroundImage: `url("${curImgUrl}")`,
								...(scrubMode === "coconut_grid" ? getCoconutGridScrubStyle() : getFioVerticalScrubStyle()),
							}}
						>
							<Box
								sx={{
									opacity: mouseHover ? 1 : 0,
									position: "relative",
									pointerEvents: "none",
									top: 0,
									height: "100%",
									width: "2px",
									transform: `translate(${mousePosition[0]}px, 0px)`,
									backgroundColor: `${theme.palette.error.main}`,
								}}
							/>

							<Box
								style={{
									opacity: mouseHover ? 0 : 1,
									position: "absolute",
									pointerEvents: "none",
									top: 0,
									bottom: 0,
									width: "100%",
									height: "100%",
									backgroundSize: "cover",
									backgroundPosition: "50% 50%",
									backgroundImage: curImgUrlCover ? `url("${curImgUrlCover}")` : undefined,
									backgroundRepeat: "no-repeat",
								}}
							/>

							<Box
								component={FilmIcon}
								sx={{
									opacity: 0.85,
									fontSize: "medium",
									pointerEvents: "none",
									position: "absolute",
									bottom: 5,
									right: 5,
								}}
							/>
						</Box>
					</Box>
				) : (
					<Box sx={{ ...(is_round && { borderRadius: 4, overflow: "hidden" }), position: "relative", width: "100%", height: "100%" }}>
						<Box
							style={{
								cursor: "pointer",
								width: "100%",
								height: "100%",
								backgroundSize: "cover",
								backgroundPosition: "50% 50%",
								backgroundImage: `url("${curImgUrl}")`,
								...(img_fit_view && { backgroundRepeat: "no-repeat", backgroundSize: "contain" }),
							}}
						>
							{isVideo ? (
								<Box
									component={FilmIcon}
									sx={{
										opacity: 0.85,
										fontSize: "medium",
										pointerEvents: "none",
										position: "absolute",
										bottom: "5px",
										right: "5px",
									}}
								/>
							) : null}
						</Box>
					</Box>
				)
			) : (
				<Skeleton
					sx={{
						...sx,
						...(is_round && { borderRadius: "18px!important", overflow: "hidden!important" }),
					}}
					variant="rectangular"
				/>
			)}
		</Box>
	);
});
